import { useState, useEffect, memo } from 'react';
import { Heart, Snowflake, Flame } from 'lucide-react';
import type { PlaylistItem } from '@/shared/types';
import { formatarRestante } from '../../domain/usecases/validarPedido';

type EstadoSong = 'disponivel' | 'fila' | 'minha_fila' | 'tocando' | 'bloqueado';

interface Props {
  item:          PlaylistItem;
  estado:        EstadoSong;
  tempoRestante: number;
  onPedir:       (item: PlaylistItem) => void;
  pedindo:       boolean;
  battleTeam?:   'romance' | 'sofrencia';
  songBtnBg?:    string;
  songBtnIcon?:  string;
  /** Item com o maior `pedidos_count` do Repertório (ver JukeboxRepertorio). */
  maisPedida?:   boolean;
  /** Selo curto ao lado do canal (ex.: "Já está na playlist" nos resultados da busca). */
  selo?:         string;
}

function formatDuracao(s: number | null | undefined): string | null {
  if (!s || s <= 0) return null;
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/**
 * Faixa do setlist — borda colorida comunica o estado do pedido.
 */
function SongCardImpl({
  item, estado, tempoRestante, onPedir, pedindo,
  battleTeam, songBtnBg = 'bg-orange-500/15 group-hover:bg-orange-500/25', songBtnIcon = 'text-orange-400',
  maisPedida, selo,
}: Props) {
  const [tempoAtual, setTempoAtual] = useState(tempoRestante);
  const duracao   = formatDuracao(item.duracao_segundos);
  const naFila    = estado === 'fila' || estado === 'minha_fila';
  const minhaFila = estado === 'minha_fila';
  const bloqueado = estado === 'bloqueado';

  useEffect(() => { setTempoAtual(tempoRestante); }, [tempoRestante]);

  useEffect(() => {
    if (!bloqueado || tempoAtual <= 0) return;
    const prox = Math.ceil(tempoAtual / 60_000) * 60_000 - tempoAtual;
    const t = setTimeout(() => setTempoAtual(p => Math.max(0, p - prox)), prox);
    return () => clearTimeout(t);
  }, [bloqueado, tempoAtual]);

  if (estado === 'tocando') return null;

  const desabilitado = naFila || bloqueado || pedindo;

  return (
    <button
      type="button"
      onClick={() => !desabilitado && onPedir(item)}
      disabled={desabilitado}
      aria-label={`${item.titulo}${naFila ? ', na fila' : bloqueado ? `, disponível em ${formatarRestante(tempoAtual)}` : ', tocar'}`}
      className={`
        group relative w-full flex items-center gap-3 overflow-hidden rounded-2xl p-3
        text-left transition-all duration-200 active:scale-[0.97]
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60
        ${bloqueado                         ? 'border border-amber-500/15 bg-amber-500/[0.05]'
        : minhaFila && battleTeam === 'romance'   ? 'border border-rose-500/25 bg-rose-500/[0.08]'
        : minhaFila && battleTeam === 'sofrencia' ? 'border border-cyan-500/25 bg-cyan-500/[0.08]'
        : minhaFila                               ? 'border border-emerald-500/20 bg-emerald-500/[0.08]'
        : naFila                                  ? 'border border-orange-500/20 bg-orange-500/[0.08]'
        : pedindo                                 ? 'scale-[0.99] border border-orange-500/20 bg-orange-500/10'
        : 'border border-white/[0.06] bg-white/[0.03] hover:border-white/[0.1] hover:bg-white/[0.06]'}
      `}
    >
      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl">
        <img
          src={item.thumbnail}
          alt=""
          aria-hidden
          loading="lazy"
          className={`h-full w-full object-cover transition-all duration-300 ${
            bloqueado ? 'grayscale opacity-30' : 'group-hover:scale-105'
          }`}
        />
        {bloqueado && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 bg-black/50">
            <svg className="h-3.5 w-3.5 text-amber-400/90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="text-[11px] font-bold leading-none text-amber-300">{formatarRestante(tempoAtual)}</span>
          </div>
        )}
        {pedindo && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
          </div>
        )}
        {duracao && !bloqueado && !pedindo && (
          <span className="absolute bottom-0.5 right-0.5 rounded bg-black/60 px-1 py-px font-mono text-[10px] leading-none text-white/70">
            {duracao}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        {maisPedida && !bloqueado && (
          <div className="mb-0.5 flex items-center gap-1 text-amber-400/90">
            <Flame size={10} fill="currentColor" strokeWidth={0} className="shrink-0" />
            <span className="text-[10px] font-semibold uppercase tracking-wide">Mais pedida</span>
          </div>
        )}
        <p className={`line-clamp-2 text-base font-semibold leading-snug ${
          bloqueado ? 'text-white/60' : minhaFila ? 'text-white' : 'text-white/90'
        }`}>{item.titulo}</p>

        <div className="mt-0.5 flex items-center gap-1.5">
          {bloqueado ? (
            <span className="text-xs text-amber-400/85">
              Disponível em {formatarRestante(tempoAtual)}
            </span>
          ) : (
            item.canal && <p className="truncate text-xs text-white/55">{item.canal}</p>
          )}
          {selo && !bloqueado && (
            <span className="shrink-0 rounded-full bg-emerald-400/15 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
              {selo}
            </span>
          )}
        </div>
      </div>

      <div key={estado} className="ml-1 shrink-0 animate-scale-in">
        {minhaFila ? (
          battleTeam === 'romance' ? (
            <div className="flex items-center gap-1 rounded-full border border-rose-500/25 bg-rose-500/15 px-2.5 py-1.5">
              <Heart size={10} fill="currentColor" strokeWidth={0} className="shrink-0 text-rose-400" />
              <span className="text-xs font-semibold text-rose-400">Apaixonado</span>
            </div>
          ) : battleTeam === 'sofrencia' ? (
            <div className="flex items-center gap-1 rounded-full border border-cyan-500/25 bg-cyan-500/15 px-2.5 py-1.5">
              <Snowflake size={10} className="shrink-0 text-cyan-400" />
              <span className="text-xs font-semibold text-cyan-400">Sofrendo</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 rounded-full border border-emerald-500/25 bg-emerald-500/15 px-2.5 py-1.5">
              <svg className="h-3 w-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-xs font-semibold text-emerald-400">Seu pedido</span>
            </div>
          )
        ) : naFila ? (
          <div className="flex items-center gap-1 rounded-full border border-orange-500/25 bg-orange-500/15 px-2.5 py-1.5">
            <svg className="h-3 w-3 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-xs font-semibold text-orange-400">Na fila</span>
          </div>
        ) : bloqueado ? (
          <div className="px-2 text-center">
            <svg className="mx-auto h-4 w-4 text-amber-500/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="mt-0.5 block text-[11px] font-bold leading-none text-amber-500/70">{formatarRestante(tempoAtual)}</span>
          </div>
        ) : (
          <div className={`flex h-11 w-11 items-center justify-center rounded-full transition-all duration-200 ${
            pedindo
              ? 'bg-orange-500/30'
              : `${songBtnBg} group-hover:scale-110`
          }`}>
            <svg className={`h-5 w-5 ${songBtnIcon}`} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z" />
            </svg>
          </div>
        )}
      </div>
    </button>
  );
}

export const SongCard = memo(SongCardImpl);
